'use client'

import type { Data } from '@repo/strapi'
import { useSearchParams } from 'next/navigation'
import { useTranslations } from 'next-intl'
import React, { useState } from 'react'
import { CaseStudyCard } from '@/components/page-builder/global/caseStudiesFilters/CaseStudyCard'
import { Button } from '@/components/ui/button'

export function CaseStudiesResults({
	results,
	reviewBannerSlot,
}: {
	results: Data.ContentType<'api::case-study.case-study'>[]
	reviewBannerSlot?: React.ReactNode
}) {
	const searchParams = useSearchParams()
	const t = useTranslations('CaseStudiesResults')
	const [showAllCaseStudies, setShowAllCaseStudies] = useState(false)
	// Extract filters from URL
	const industryFilters = searchParams.getAll('industry')
	const integrationsFilters = searchParams.getAll('integrations')
	const companyFilters = searchParams.getAll('company_size')
	const languageFilters = searchParams.getAll('target_languages')

	const matchesFilter = (filters: string[], values: string[]) => {
		if (filters.length === 0) return true
		// OR within category
		return filters.some((f) => values.includes(f))
	}

	const filteredCaseStudies = results.filter((caseStudy) => {
		const industry = caseStudy.industry?.industry ? [caseStudy.industry.industry] : []
		const company = caseStudy.companySize?.companySize ? [caseStudy.companySize.companySize] : []
		const integrations = caseStudy.integrations?.map((i) => i.documentId) ?? []
		const languages =
			(caseStudy.languages?.map((l) => l.language).filter(Boolean) as string[]) ?? []

		const matchesIndustry = matchesFilter(industryFilters, industry)
		const matchesCompany = matchesFilter(companyFilters, company)
		const matchesIntegrations = matchesFilter(integrationsFilters, integrations)
		const matchesLanguages = matchesFilter(languageFilters, languages)

		// AND across categories
		return matchesIndustry && matchesCompany && matchesIntegrations && matchesLanguages
	})

	const visibleCaseStudies = showAllCaseStudies
		? filteredCaseStudies
		: filteredCaseStudies.slice(0, 12)

	return (
		<div className="flex flex-col gap-10">
			<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3  gap-x-10 lg:gap-y-15 gap-y-10">
				{visibleCaseStudies.map((caseStudy, index) => (
					<React.Fragment key={caseStudy.id}>
						<CaseStudyCard
							slug={caseStudy.slug}
							title={caseStudy.title}
							primaryImage={caseStudy.heroBanner?.primaryImage}
							previewImage={caseStudy?.previewImage}
							brandTitle={caseStudy.brandTitle}
						/>

						{reviewBannerSlot && index === 5 ? reviewBannerSlot : null}
					</React.Fragment>
				))}

				{reviewBannerSlot && visibleCaseStudies.length <= 6 ? reviewBannerSlot : null}
			</div>
			{!showAllCaseStudies && filteredCaseStudies.length > 12 ? (
				<Button className="mx-auto" onClick={() => setShowAllCaseStudies(true)}>
					{t('showMore')}
				</Button>
			) : null}
		</div>
	)
}
